/* global document window */
//navigation panel js goes here

import $ from "jquery";
import { highContrastToggle } from "../high-contrast.js";
import { compactHeaderCheck } from "common/page/header/header.js";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

/**
 * Selectors that match all of the dialogs embedded in the navigation panel.
 *
 * @const
 */
const selectorNavigationPanelDialogs = [
  ".c-navigation-panel-dialog-accessibility",
  ".c-navigation-panel-dialog-accessibility *",
  ".c-navigation-panel-dialog-languages",
  ".c-navigation-panel-dialog-languages *"
].join(", ");

// Declare pre-stored DOM elements.
//
// While the elements aren't available until the DOM is ready (so they can't be
// selected here), they must be declared here in order to bring them into the
// global namespace of this file, as otherwise they would not be available to
// the rest of the functions which rely on them.

let elOpacityOverlay;
let elAccessibilityOpenButton;
let elAccessibilityCloseButton;
let elHighContrastToggleButton;
let elLanguagesOpenButton;
let elLanguagesCloseButton;
let elAccessibilityDialog;
let elLanguagesDialog;
let elNavigationPanel;
let elNavigationPanelContainer;
let elHamburgerToggle;
let elNavigationPanelInteractableAreas;
//let elBody;

// Modifiers
const modifierAccessibilityDialogOpen = "c-navigation-panel-dialog--open";
const modifierOpacityOverlayOpen = "c-navigation-panel-opacity-overlay--open";
const modifierNavigationPanelOpen = "c-navigation-panel--open";
const navPanelTransitionDuration = 600;

// Defer until launch
$(() => {
  // Pre-store selectors as DOM elements
  //
  // They were declared before without value, and now a value must be assigned
  // to each of them.
  elOpacityOverlay = $(".c-navigation-panel-opacity-overlay");
  elAccessibilityOpenButton = $(".c-navigation-panel__footer-accessibility-link");
  elAccessibilityCloseButton = $(".c-navigation-panel-dialog__close-button-accessibility");
  elHighContrastToggleButton = $(".c-navigation-panel-dialog__hc-toggle");
  elLanguagesOpenButton = $(".c-navigation-panel__footer-languages-button");
  elLanguagesCloseButton = $(".c-navigation-panel-dialog__close-button-languages");
  elAccessibilityDialog = $(".c-navigation-panel-dialog-accessibility");
  elLanguagesDialog = $(".c-navigation-panel-dialog-languages");
  elNavigationPanelContainer = $(".c-navigation-panel");
  elNavigationPanel = document.querySelector("#c-navigation-panel");
  elHamburgerToggle = $(".c-header__hamburger");
  elNavigationPanelInteractableAreas = $(".c-navigation-panel__body, .c-navigation-panel__header");

  // Bind misc
  //

  // Bind clicks on the overlay to close the navigation panel entirely.
  elOpacityOverlay.on("click keypress", navigationPanelClose);

  // Bind accessibility dialog
  //

  //click on the accessibility icon to open the accessiblity dialog
  elAccessibilityOpenButton.on("click", accessibilityDialogOpen);

  //click on the close button of the accessibility dialog
  elAccessibilityCloseButton.on("click", navigationPanelClose);

  //click on the high contrast toggle
  elHighContrastToggleButton.on("click", highContrastToggle);

  // Bind languages dialog
  //

  //click on the language link to open the choose language dialog
  elLanguagesOpenButton.on("click", languagesDialogOpen);

  //click on the close button of the language dialog
  elLanguagesCloseButton.on("click", navigationPanelClose);
});


/**
 * Open the accessibility dialog (in navigation panel)
 *
 * @returns {undefined}
 */
function accessibilityDialogOpen() {
  navigationPanelPartialClose();
  elAccessibilityOpenButton
    .attr("aria-expanded", "true");
  elAccessibilityDialog.toggleClass(modifierAccessibilityDialogOpen);
}

/**
 * Close the accessibility dialog (in navigation panel)
 *
 * @returns {undefined}
 */
function accessibilityDialogClose() {
    elAccessibilityDialog
      .removeClass(modifierAccessibilityDialogOpen);

    elOpacityOverlay
      .removeClass(modifierOpacityOverlayOpen);

    elAccessibilityOpenButton
      .attr("aria-expanded", "false");
}

/**
 * Open the languages dialog
 *
 * @returns {undefined}
 */
function languagesDialogOpen() {
  navigationPanelPartialClose();

  elLanguagesOpenButton
    .attr("aria-expanded", "true")
    .attr("aria-hidden", "false")
    .focus()
    .find("a, button")
    .attr("tabindex", "0");
  elLanguagesDialog
    .addClass(modifierAccessibilityDialogOpen);
}

/**
 * Close the languages dialog
 *
 * @returns {undefined}
 */
function languagesDialogClose() {
  elLanguagesOpenButton
    .attr("aria-expanded", "false");
  elLanguagesDialog
    .removeClass(modifierAccessibilityDialogOpen);
}

/**
 * Only close the navigation panel, without closing any dialogs or the opacity
 * overlay.
 *
 * @returns {undefined}
 */
function navigationPanelPartialClose() {
  elNavigationPanelContainer
    .removeClass(modifierNavigationPanelOpen)
    // Set all buttons to be un-tabblable, except if they're parts of child dialogs
    .find("a, button")
    .not(selectorNavigationPanelDialogs)
    .attr("tabindex", -1);

    //scroll the navigation panel back up
    window.setTimeout(() => elNavigationPanelContainer.scrollTop(0), navPanelTransitionDuration);

  $()
    .attr("aria-hidden", "true");

  // Unbind escape
  $(document).off("keydown.navigationPanelCloseEsc");
}

/**
 * Open the navigation panel.
 *
 * @returns {undefined}
 */
export function navigationPanelOpen() {
  // Set attributes for the navigation panel
  elNavigationPanelContainer
    .addClass(modifierNavigationPanelOpen)
    .attr("aria-hidden", "false")
  // Set all buttons to be tabbable.
    .find("a, button").attr("tabindex", 0);

  elNavigationPanelInteractableAreas
    .attr("aria-hidden", "false");

  $(document).on("keydown.navigationPanelCloseEsc", (ev) => {
    if (ev.key === "Escape") {
      navigationPanelClose();
    }
  });

  // Set attributes for the overlay
  elOpacityOverlay
    .addClass(modifierOpacityOverlayOpen);

  elHamburgerToggle
    .attr("aria-expanded", "true")
    .attr("aria-checked", "true");

  //Lock scrolling on the page
  disableBodyScroll(elNavigationPanel);
}

/**
 * Close the navigation panel entirely
 *
 * @returns {undefined}
 */
function navigationPanelClose() {
  navigationPanelPartialClose();

  // Remove overlay
  elOpacityOverlay
    .removeClass(modifierOpacityOverlayOpen);

  languagesDialogClose();
  accessibilityDialogClose();

  elNavigationPanelContainer
    .attr("aria-hidden", "true");

  elHamburgerToggle
    .attr("aria-expanded", "false")
    .attr("aria-checked", "false");

  compactHeaderCheck();

  //Reenable scrolling on the page
  enableBodyScroll(elNavigationPanel);
}

export function isNavigationPanelOpen() {
  return elNavigationPanelContainer.hasClass(modifierNavigationPanelOpen);
}

export function isDialogOpen() {
  return elAccessibilityDialog.hasClass(modifierAccessibilityDialogOpen) || elLanguagesDialog.hasClass(modifierAccessibilityDialogOpen);
}



// WEBPACK FOOTER //
// ./src/frontend/common/page/navigation-panel/navigation-panel.js